import {type ComponentProps, useCallback, useEffect, useRef} from 'react';
import {X} from 'lucide-react';
import {Textarea as BaseTextarea} from '@ui/shadcn/textarea';

/**
 * Обёртка над shadcn Textarea с кнопкой очистки (иконка X).
 * Не вызывает ре-рендеров при вводе — кнопка управляется через DOM.
 */
export const Textarea = ({
  className,
  ...props
}: ComponentProps<typeof BaseTextarea>) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const sync = useCallback(() => {
    const el = textareaRef.current;
    const btn = btnRef.current;
    if (!el || !btn) return;
    const show = el.value.length > 0 && !props.readOnly && !props.disabled;
    btn.style.display = show ? '' : 'none';
    el.style.paddingRight = show ? '1.75rem' : '';
  }, [props.readOnly, props.disabled]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    sync();
    el.addEventListener('input', sync);
    return () => el.removeEventListener('input', sync);
  }, [sync]);

  const handleClear = () => {
    const el = textareaRef.current;
    if (!el || el.readOnly || el.disabled) return;
    const setter = Object.getOwnPropertyDescriptor(
      HTMLTextAreaElement.prototype,
      'value',
    )?.set?.bind(el);
    if (setter) {
      setter('');
      el.dispatchEvent(new Event('input', {bubbles: true}));
    }
    sync();
    el.focus();
  };

  return (
    <div className='relative w-full'>
      <BaseTextarea ref={textareaRef} className={className} {...props} />
      <button
        ref={btnRef}
        type='button'
        onClick={handleClear}
        disabled={props.disabled || props.readOnly}
        tabIndex={-1}
        aria-label='Очистить'
        style={{display: 'none'}}
        className='absolute right-1.5 top-1.5 p-0.5 rounded-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors'
      >
        <X size={13} />
      </button>
    </div>
  );
};
